import type {
    TResponseGetDeal,
    TResponseGetDealFiles,
} from "@/apis/odin/apiODIN";
import { isNotNullable, isNullOrUndefined } from "@/utils/guards";

type ProjectActivity = {
    user: string;
    action: string;
    timestamp: string;
};

export const getProjectActivityList = ({
    projectPropertyInfo,
    projectFilesWithDeletedExtractions,
}: {
    projectPropertyInfo: TResponseGetDeal | null;
    projectFilesWithDeletedExtractions: TResponseGetDealFiles["files"];
}): ProjectActivity[] => {
    const makeDescriptions = (name: string) => ({
        file: {
            create: () => `Added: ${name}`,
            modified: () => `Modified: ${name}`,
            delete: () => `Deleted: ${name} from project`,
        },
        extraction: {
            create: (extractionName: string) =>
                `Extracted from ${name}: ${extractionName}`,
            modified: (extractionName: string) =>
                `Modified extraction from ${name}: ${extractionName}`,
            delete: (extractionName: string) =>
                `Deleted extraction from ${name}: ${extractionName}`,
        },
        project: {
            create: () => `Created new project: ${name}`,
            modified: () => `Modified project: ${name}`,
        },
    });

    let activityList: ProjectActivity[] = [];
    if (projectPropertyInfo) {
        const projectDescriptions = makeDescriptions(
            projectPropertyInfo.dealName,
        );
        const { modifiedAt, modifiedBy, createdAt, createdBy } =
            projectPropertyInfo;

        activityList = [
            ...activityList,
            {
                user: createdBy,
                action: projectDescriptions.project.create(),
                timestamp: createdAt,
            },
        ];

        if (
            isNotNullable(modifiedAt) &&
            isNotNullable(modifiedBy) &&
            modifiedBy !== createdBy &&
            modifiedAt !== createdAt
        ) {
            activityList = [
                ...activityList,
                {
                    user: modifiedBy,
                    action: projectDescriptions.project.modified(),
                    timestamp: modifiedAt,
                },
            ];
        }
    }
    projectFilesWithDeletedExtractions.forEach(file => {
        const fileDescriptions = makeDescriptions(
            file.fileInfo.originalFilename,
        );

        activityList = [
            ...activityList,
            {
                user: file.fileInfo.uploadedBy,
                action: fileDescriptions.file.create(),
                timestamp: file.fileInfo.uploadedAt,
            },
        ];

        if (
            file.fileInfo.deletedAt !== undefined &&
            file.fileInfo.deletedAt !== null
        ) {
            activityList = [
                ...activityList,
                {
                    user: file.fileInfo.uploadedBy,
                    action: fileDescriptions.file.delete(),
                    timestamp: file.fileInfo.deletedAt,
                },
            ];
        }
        file.extractions.forEach(extraction => {
            activityList = [
                ...activityList,
                {
                    user: extraction.createdBy,
                    action: fileDescriptions.extraction.create(
                        `${extraction.sheetName} Table ${extraction.extractionId}`,
                    ),
                    timestamp: extraction.createdAt,
                },
            ];
            if (
                extraction.modifiedAt !== undefined &&
                extraction.modifiedAt !== null &&
                extraction.modifiedBy !== undefined &&
                extraction.modifiedBy !== null
            ) {
                activityList = [
                    ...activityList,
                    {
                        user: extraction.modifiedBy,
                        action: !isNullOrUndefined(extraction.deletedAt)
                            ? fileDescriptions.extraction.delete(
                                  `${extraction.sheetName} Table ${extraction.extractionId}`,
                              )
                            : fileDescriptions.extraction.modified(
                                  `${extraction.sheetName} Table ${extraction.extractionId}`,
                              ),
                        timestamp: extraction.modifiedAt,
                    },
                ];
            }
        });
    });
    return activityList
        .toSorted(
            (a, b) =>
                new Date(b.timestamp).getTime() -
                new Date(a.timestamp).getTime(),
        )
        .map(r => ({
            ...r,
            timestamp: new Date(r.timestamp).toISOString(),
        }));
};
